import * as JSPlumb from 'jsplumb';
import { DependencyEditmodeService } from '../services/dependency-editmode.service';
import { Criticality } from './enums/criticality';

/**
 * Model to handle the data of a drawn dependency-line
 */
export class DrawnDependency {

  public static CONNECTOR_CSS_CLASS = 'drawn-dependency';
  public static CONNECTOR_SOURCE_CSS_CLASS = 'source-issue-id-';
  public static CONNECTOR_TARGET_CSS_CLASS = 'target-issue-id-';
  public static CONNECTOR_DEPENDENCY_ID_CSS_CLASS = 'dependency-';
  public static DEPENDENCY_DELETE_CSS_CLASS = 'dependency-delete';
  public static CONNECTOR_HIDDEN_CSS_CLASS = 'hidden';
  public static CONNECTOR_NORMAL_DEPENDENCY_CLASS = Criticality.Normal + '-dependency';
  public static CONNECTOR_CRITICAL_DEPENDENCY_CLASS = Criticality.Critical + '-dependency';
  public static CONNECTOR_BLOCKING_DEPENDENCY_CLASS = Criticality.Blocking + '-dependency';
  public sourceIssueId: number;
  public targetIssueId: number;
  public criticality: Criticality;
  public issueLinkId: number;
  public line: JSPlumb.Connection;
  private onDeleteButtonCallback: any;

  constructor(
    sourceIssueId: number,
    targetIssueId: number,
    issueLinkId: number,
    criticality: Criticality,
    jsPlumbInstance: JSPlumb.jsPlumbInstance,
    visible: boolean,
    onDeleteButtonCallback: any
  ) {
    this.createDeleteButtonForOverlay = this.createDeleteButtonForOverlay.bind(this);
    this.sourceIssueId = Number(sourceIssueId);
    this.targetIssueId = Number(targetIssueId);
    this.criticality = criticality;
    this.issueLinkId = Number(issueLinkId);
    this.onDeleteButtonCallback = onDeleteButtonCallback;
    this.createLine(jsPlumbInstance, visible);
  }

  public removeLine(jsPlumbInstance: JSPlumb.jsPlumbInstance): void {
    jsPlumbInstance.deleteConnection(this.line);
  }

  public updateDeleteButtonVisibility(): void {
    if (!this.line) {
      return;
    }
    if (DependencyEditmodeService.editModeActive) {
      this.line.showOverlay(this.sourceIssueId.toString());
    }
    else {
      this.line.hideOverlay(this.sourceIssueId.toString());
    }
  }

  private createLine(jsPlumbInstance: JSPlumb.jsPlumbInstance, visible: boolean): void {
    const sourceElement = document.querySelector(`app-issue > [data-issueid="${this.sourceIssueId}"]`);
    const targetElement = document.querySelector(`app-issue > [data-issueid="${this.targetIssueId}"]`);

    let line: JSPlumb.Connection;
    if (sourceElement && targetElement && (this.sourceIssueId !== this.targetIssueId)) {
      let lineCssClasses = DrawnDependency.CONNECTOR_CSS_CLASS;
      lineCssClasses += ' ' + DrawnDependency.CONNECTOR_SOURCE_CSS_CLASS + this.sourceIssueId;
      lineCssClasses += ' ' + DrawnDependency.CONNECTOR_TARGET_CSS_CLASS + this.targetIssueId;
      lineCssClasses += ' ' + DrawnDependency.CONNECTOR_DEPENDENCY_ID_CSS_CLASS + this.issueLinkId;
      if (!visible) {
        lineCssClasses += ' ' + DrawnDependency.CONNECTOR_HIDDEN_CSS_CLASS;
      }

      switch (String(this.criticality)) {
        case Criticality.Normal: {
          lineCssClasses += ' ' + DrawnDependency.CONNECTOR_NORMAL_DEPENDENCY_CLASS;
          break;
        }
        case Criticality.Critical: {
          lineCssClasses += ' ' + DrawnDependency.CONNECTOR_CRITICAL_DEPENDENCY_CLASS;
          break;
        }
        case Criticality.Blocking: {
          lineCssClasses += ' ' + DrawnDependency.CONNECTOR_BLOCKING_DEPENDENCY_CLASS;
          break;
        }
      }

      line = jsPlumbInstance.connect(
        // Attention: We switch source and target here for the drawing, because we want to draw
        // the arrow as 'blocked by' - Arrow, but our issues are based on 'blocks'-data.
        // i.e. data says 'Source blocks Target' but we show 'Target blocked by Source'
        {
          source: targetElement, target: sourceElement,
          cssClass: lineCssClasses,
          endpoint: ['Dot', { radius: 5, cssClass: lineCssClasses }],
        }
      );
      // Set the second endpoint to 'Blank' as we show an Arrow-Overlay there
      line.endpoints[1].setEndpoint(['Blank', {}]);
      line.addOverlay(['Arrow', { direction: 1, location: 1, cssClass: lineCssClasses + ' line-connector' }]);
      line.addOverlay(['Custom', this.getDeleteButtonOverlay()]);
      this.line = line;
      this.updateDeleteButtonVisibility();
    }
    else {
      this.line = null;
    }
  }

  /**
   * Returns a JSPlumb.OverlayOptions-Object containing the Delete-Button-Overlay
   */
  private getDeleteButtonOverlay(): JSPlumb.OverlayOptions {
    return {
      create: this.createDeleteButtonForOverlay,
      location: 0.5,
      // we use the sourceIssueId as the id for this overlay too
      id: this.sourceIssueId,
      events: {
        click: this.onDeleteButtonCallback
      }
    };
  }

  private createDeleteButtonForOverlay(): HTMLElement {
    // mat-icon
    const icon = document.createElement('mat-icon');
    icon.setAttribute('role', 'img');
    icon.setAttribute('aria-hidden', 'true');
    icon.className = 'mat-icon notranslate material-icons mat-icon-no-color';
    icon.innerText = 'delete_outline';

    // span
    const span = document.createElement('span');
    span.className = 'mat-button-wrapper';
    span.appendChild(icon);

    // button
    const button = document.createElement('button');
    button.className = 'mat-focus-indicator mat-mini-fab mat-button-base mat-accent';
    button.className += ' ' + DrawnDependency.DEPENDENCY_DELETE_CSS_CLASS;
    button.className += ' ' + DrawnDependency.CONNECTOR_SOURCE_CSS_CLASS + this.sourceIssueId;
    button.className += ' ' + DrawnDependency.CONNECTOR_TARGET_CSS_CLASS + this.targetIssueId;
    button.setAttribute('mat-mini-fab', '');
    button.setAttribute('color', 'accent');
    button.setAttribute('ng-reflect-color', 'accent');
    button.appendChild(span);

    return button;
  }
}


